import React from 'react';
import {
  RegistrationScreen,
  formatDob,
  type RegistrationConfig,
} from './registration-common.js';

/**
 * SCRN 18 — Business24 Registration (CCF-57). Mirrors the Figma
 * `update-business-24-reg` design: name / DoB / mother's maiden name / mobile
 * mandatory identity items, then any 2 of 3 additional questions (first school
 * attended / favourite music / eye colour), 2FA, mandatory scripting and
 * "Transfer to automated self-service". Reached after a deregistration when the
 * online-card type is BUSINESS24 (CCF-8/CCF-46).
 *
 * The 3 additional questions' on-file answers are `additionalAnswersUnordered`
 * (issue #305, Dylan's refined requirement, 2026-07-09): they are free text
 * from `customer/info`, with no guaranteed order or 1:1 mapping to the 3
 * questions below (an agent free-types into the source field elsewhere) — so
 * no `answer` resolver is wired per-question; `RegistrationScreen` renders the
 * on-file answers as a single, unordered list instead.
 *
 * `notOnFileToggles` (issue #333, per CCF-57's acceptance criteria): the
 * maiden-name row carries a "Mother's maiden name not on file" tick-box that
 * reveals "Do you have any other products or accounts with PTSB?" (answered
 * from the same `OtherProducts` data Open24 uses); the mobile row carries a
 * "Mobile number not on file" tick-box that reveals the landline on file
 * (`Landline`). This reuses the shared toggle mechanism `open24Extras` drives
 * for Open24, WITHOUT pulling in Open24's other extras (cards dropdown,
 * joint-accounts confirmatory check, CCF-16 2FA read-back script) — none of
 * which CCF-57 calls for.
 */
const CONFIG: RegistrationConfig = {
  screenCode: 'SCRN-18',
  heading: 'Business 24 registration',
  notOnFileToggles: true,
  // #774 (@dylan219, 2026-09-11): the mobile-number question is always markable
  // Pass/Fail with no alternate question; a mobile FAIL auto-fails 2FA (outcome
  // deferred until all other questions are completed). Supersedes the mobile
  // "not on file" → landline-alternate behaviour of #348/#656/#662/#372 (the
  // maiden "not on file" toggle is unaffected and stays).
  mobileQuestionAlwaysMarkable: true,
  mandatory: [
    { key: 'name', label: 'Full name', value: (_q, name) => name },
    { key: 'dob', label: 'Date of birth', value: (q) => formatDob(q.Dob) },
    {
      key: 'maiden',
      label: "Mother's maiden name",
      value: (q) => q.MothersMaidenName,
    },
    {
      key: 'mobile',
      label: 'Mobile number',
      value: (q) => q.MobileNumber ?? q.MaskedMobile,
    },
  ],
  additional: [
    { key: 'school', label: 'Can you confirm the first school you attended?' },
    {
      key: 'music',
      label: 'Can you confirm your favourite type of music?',
    },
    { key: 'eye', label: 'Can you confirm your eye colour?' },
  ],
  requiredAdditional: 2,
  // Issue #648 (@dylan219, 2026-08-17): a tick-box in front of each of the 3
  // additional questions; the agent selects EXACTLY 2, mirroring the Mortgage
  // multi-select model (#350). Only a ticked question's Pass/Fail control is
  // shown, a 3rd selection is prevented, and the outcome passes only when both
  // selected are Pass. `additionalAlwaysMarkable` (Option A) makes a ticked
  // question always markable — the agent verbally verifies against the
  // free-text "answers on file" list — superseding the #305 "≥2 distinct
  // on-file answers" enable gate (which disabled the controls whenever the real
  // gateway's free-text field was empty, the reported symptom).
  additionalSelectableLimit: 2,
  additionalAlwaysMarkable: true,
  additionalInstructions:
    'Tick the box beside the 2 questions you are asking the customer, then ' +
    'mark each Pass or Fail against the answers on file shown below.',
  twoFactor: true,
  additionalAnswersUnordered: true,
  // #653 — once all questions (+ 2FA) pass, offer the Reset Internet Password
  // option with a scoped Back round-trip (@dylan219).
  ipwReset: true,
};

export function Business24RegistrationScreen(): React.JSX.Element {
  return <RegistrationScreen config={CONFIG} />;
}
